import React, { useRef, useState, Suspense } from 'react'
import lazyWithRetry from '../../utils/lazyWithRetry'
import '../../styles/management/ManageTeacher.css'
import '../../styles/management/ClassList.css'
import '../../styles/management/TeacherList.css'
import '../../styles/management/ClassHome.css'
import '../../styles/management/ManagementHome.css'
import { useUser } from '../../auth'
import { useInstitution } from '../../auth/InstitutionContext'
import { usePageTitle } from '../../utils/util'
import { useYearClassFilter } from '../../hooks/useYearClassFilter'
import { oneOfCodec, useUrlState } from '../../hooks/useUrlState'
import { useElementWidthBand } from '../../hooks/useElementWidth'
import { useAppContext } from '../../context/AppContext'
import SkeletonManagementHeader from './skeleton/SkeletonManagementHeader'
import SkeletonClassList from './skeleton/SkeletonClassItem'
import SkeletonManagementNavBar from './skeleton/SkeletonManagementNavBar'
import { isItAccess } from '../../constants/Roles'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'
import { ADMIN_BUCKET, IT_ACCESS_BUCKET, NO_ROLE_BUCKET } from './teacherRoleBuckets'

const ManagementHeader = lazyWithRetry(() => import('./ManagementHeader'), './ManagementHeader')

const ClassManagement = lazyWithRetry(() => import('./ClassManagement'), './ClassManagement')

const TeacherManagement = lazyWithRetry(() => import('./TeacherManagement'), './TeacherManagement')

const InactivityPanel = lazyWithRetry(() => import('../inactivity/InactivityPanel'), '../inactivity/InactivityPanel')

const ManagementNavBar = lazyWithRetry(() => import('./ManagementNavBar'), './ManagementNavBar')

const InactivitySettingsModal = lazyWithRetry(() => import('../inactivity/InactivitySettingsModal'), '../inactivity/InactivitySettingsModal')

const MANAGEMENT_TYPES = ['class', 'teacher', 'inactivity']

const PAGE_TITLES = {
    class: 'Upstrive Education - User management',
    teacher: 'Upstrive Education - User management',
    inactivity: 'Upstrive Education - Inactivity',
}

const TEACHER_PSEUDO_YEARS = [ADMIN_BUCKET, IT_ACCESS_BUCKET, NO_ROLE_BUCKET]

// Layout bands for the page, measured on the page itself rather than the
// viewport: the sidebar is 280px pinned open and 80px collapsed, so the same
// screen can leave this page anywhere from ~690px to 1500px+. Below `compact`
// the header and class cards stop fitting on one row (e.g. iPad Pro 13" with
// the sidebar pinned); below `narrow` their paddings and type step down too.
// 0 means "not measured yet" - render the wide layout rather than flash a
// narrow one.
const COMPACT_BELOW = 1240
const NARROW_BELOW = 1000
const toLayoutBand = (width) => (!width || width >= COMPACT_BELOW ? 'wide' : width >= NARROW_BELOW ? 'compact' : 'narrow')
const LAYOUT_CLASSES = { wide: '', compact: 'is-compact', narrow: 'is-compact is-narrow' }

function ManagementHome() {
    const [managementType, setManagementType] = useUrlState('tab', 'class', oneOfCodec(MANAGEMENT_TYPES))
    // Owned here rather than in the tab bodies: all three share the single
    // nav-row pill, and two components bound to the same ?search= param would
    // fight over it.
    const [searchText, setSearchText] = useUrlState('search', '', { debounce: 700 })

    // Both dropdowns render in ManagementNavBar (next to each tab's own action
    // button), but their year/class options depend on data only the owning
    // panel has loaded, so those are reported back up via onFilterOptionsChange.
    const [inactivityFilterOptions, setInactivityFilterOptions] = useState({ filterYears: [], classesOfYear: {} })
    const inactivityFilter = useYearClassFilter('years', 'classes', inactivityFilterOptions)

    const [classFilterOptions, setClassFilterOptions] = useState({ filterYears: [], classesOfYear: {} })
    const classFilter = useYearClassFilter('classYears', 'classNames', classFilterOptions)

    const [teacherFilterOptions, setTeacherFilterOptions] = useState({ filterYears: [], classesOfYear: {} })
    const teacherFilter = useYearClassFilter('teacherYears', 'teacherClasses', teacherFilterOptions, TEACHER_PSEUDO_YEARS)

    const { institution } = useInstitution()
    const { appState } = useAppContext()
    const [layoutRef, layoutBand] = useElementWidthBand(toLayoutBand)
    const isClassLink = !!institution?.classlinkConfig?.active || !!institution?.wondeConfig?.active
    const { user } = useUser()
    const timeout = useRef(null)
    const searchRef = useRef()
    const [newTeacherOpen, setNewTeacherOpen] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState(false)
    const childRef = useRef(null)
    const managementEvents = {
        class: ANALYTICS_EVENTS.MANAGEMENT_CLASSES_VIEWED,
        teacher: ANALYTICS_EVENTS.MANAGEMENT_TEACHERS_VIEWED,
        inactivity: ANALYTICS_EVENTS.MANAGEMENT_INACTIVITY_VIEWED,
    }
    useTrackedSurface(managementEvents[managementType])

    usePageTitle(PAGE_TITLES[managementType] || PAGE_TITLES.class)

    const primaryUI = !!appState?.primary

    const addNewClass = () => {
        childRef.current.addNewClass()
    }

    // The active tab body applies the term itself (server-side for classes and
    // teachers, client-side for inactivity) through the shared `search` handle.
    const handleChange = (e) => {
        const value = e.target.value
        setSearchText(value)
        if (timeout.current !== null) {
            clearTimeout(timeout.current)
        }
        timeout.current = setTimeout(() => {
            childRef.current?.search(value)
        }, 700)
    }

    const hideTeacherButton = !(user?.role == 'school_admin' || user?.role_coverage?.target_text == 'All students' || isItAccess(user)) || primaryUI
    const isAdmin = user?.role == 'school_admin' || user?.role_coverage?.target_text?.toLowerCase() === 'all students' || isItAccess(user)

    return (
        <div id="managementId" ref={layoutRef} className={`management-home-container bg-[#ecebf8] ${LAYOUT_CLASSES[layoutBand]}`}>
            {!primaryUI && (
                <Suspense fallback={<SkeletonManagementHeader />}>
                    <ManagementHeader />
                </Suspense>
            )}
            <div className="managements-lists-container xxs:max-w-[95%] xs:max-w-[95%] md:max-w-[95%] mx-auto 3xl:max-w-[1480px] pb-[20px]">
                <Suspense fallback={<SkeletonManagementNavBar />}>
                    <ManagementNavBar
                        managementType={managementType}
                        setManagementType={setManagementType}
                        hideTeacherButton={hideTeacherButton}
                        setNewTeacherOpen={setNewTeacherOpen}
                        searchText={searchText}
                        handleChange={handleChange}
                        searchRef={searchRef}
                        isClassLink={isClassLink}
                        isAdmin={isAdmin}
                        addNewClass={addNewClass}
                        openInactivitySettings={() => setSettingsOpen(true)}
                        filterYears={inactivityFilterOptions.filterYears}
                        selectedYearFilter={inactivityFilter.selectedYearFilter}
                        selectYearFilter={inactivityFilter.selectYearFilter}
                        selectedClassFilter={inactivityFilter.selectedClassFilter}
                        selectedYearList={inactivityFilter.selectedYearList}
                        selectAllClasses={inactivityFilter.selectAllClasses}
                        selectClass={inactivityFilter.selectClass}
                        applyInactivityFilter={inactivityFilter.applyFilter}
                        resetInactivityFilter={inactivityFilter.resetFilter}
                        classFilterYears={classFilterOptions.filterYears}
                        classSelectedYearFilter={classFilter.selectedYearFilter}
                        classSelectYearFilter={classFilter.selectYearFilter}
                        classSelectedClassFilter={classFilter.selectedClassFilter}
                        classSelectedYearList={classFilter.selectedYearList}
                        classSelectAllClasses={classFilter.selectAllClasses}
                        classSelectClass={classFilter.selectClass}
                        classApplyFilter={classFilter.applyFilter}
                        classResetFilter={classFilter.resetFilter}
                        teacherFilterYears={teacherFilterOptions.filterYears}
                        teacherSelectedYearFilter={teacherFilter.selectedYearFilter}
                        teacherSelectYearFilter={teacherFilter.selectYearFilter}
                        teacherSelectedClassFilter={teacherFilter.selectedClassFilter}
                        teacherSelectedYearList={teacherFilter.selectedYearList}
                        teacherSelectAllClasses={teacherFilter.selectAllClasses}
                        teacherSelectClass={teacherFilter.selectClass}
                        teacherApplyFilter={teacherFilter.applyFilter}
                        teacherResetFilter={teacherFilter.resetFilter}
                    />
                </Suspense>
                {managementType === 'teacher' && (
                    <Suspense fallback={<SkeletonClassList />}>
                        <TeacherManagement
                            ref={childRef}
                            initialSearch={searchText}
                            newTeacherOpen={newTeacherOpen}
                            setNewTeacherOpen={setNewTeacherOpen}
                            appliedYearFilter={teacherFilter.appliedYearFilter}
                            appliedClassFilter={teacherFilter.appliedClassFilter}
                            onFilterOptionsChange={setTeacherFilterOptions}
                        />
                    </Suspense>
                )}
                {managementType === 'class' && (
                    <Suspense fallback={<SkeletonClassList />}>
                        <ClassManagement
                            ref={childRef}
                            initialSearch={searchText}
                            appliedYearFilter={classFilter.appliedYearFilter}
                            appliedClassFilter={classFilter.appliedClassFilter}
                            onFilterOptionsChange={setClassFilterOptions}
                        />
                    </Suspense>
                )}
                {managementType === 'inactivity' && (
                    <Suspense fallback={<SkeletonClassList />}>
                        <InactivityPanel
                            ref={childRef}
                            initialSearch={searchText}
                            isAdmin={isAdmin}
                            onOpenSettings={() => setSettingsOpen(true)}
                            appliedYearFilter={inactivityFilter.appliedYearFilter}
                            appliedClassFilter={inactivityFilter.appliedClassFilter}
                            onFilterOptionsChange={setInactivityFilterOptions}
                        />
                    </Suspense>
                )}
            </div>
            {managementType === 'inactivity' && isAdmin && (
                <Suspense fallback={null}>
                    <InactivitySettingsModal
                        modalIsOpen={settingsOpen}
                        closeModal={() => {
                            setSettingsOpen(false)
                            // The threshold and holiday rules decide who counts as
                            // inactive, so the list behind the modal is now stale.
                            childRef.current?.reload()
                        }}
                    />
                </Suspense>
            )}
        </div>
    )
}

export default ManagementHome
